import type {CSSProperties} from 'react';
import {font, radius, TOUCH_MIN, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';
import {wedgeFormFieldStyle} from './wedgeFormField.js';

export type WedgeMeetingLook = 'meeting-duration' | 'meeting-toggle' | 'meeting-remove' | 'meeting-add' | 'meeting-suggest';

const MEETING_LOOKS = new Set<string>(['meeting-duration', 'meeting-toggle', 'meeting-remove', 'meeting-add', 'meeting-suggest']);
export const isWedgeMeetingLook = (look: string): look is WedgeMeetingLook => MEETING_LOOKS.has(look);

/* Only the Add button keeps the transparent 44px target. The duration chip and the switch are native buttons that are 44px tall already
   (minimum height 44, pulled back by a negative margin so the visible pill stays small), so a second region could only repeat the box.
   The remove mark is an 11px glyph inside an attendee chip, and the suggestion chip is a pill 32px tall in Chromium: both wrap in rows
   38px apart (12px of padding, 2px of border, one line of type at line-height normal and a 6px gap), so two 44px regions overlap by 6px
   and a tap near a row edge could remove or add the wrong address. */
export const wedgeMeetingDrawsHit = (look: string): boolean => look === 'meeting-add' || !isWedgeMeetingLook(look);

/* Released Schedule meeting sheet (the form the Brief, Calendar, Outreach and Scout open): five native buttons. Each reads the exact legacy
   roles and keeps the declaration order of the source, so the shipped ink rules keep matching the serialized text. None sets a font family
   or size the source leaves out; the native button face stays. The caller owns the visible pill that sits inside the duration chip and the
   switch (a span), the glyph, the labels, the handlers and the layout.
   The duration chip and the switch are bare 44px boxes with a negative margin: the chip pulls back 6px above and below and 3px at each
   side, the switch 4px above and below; the pill inside keeps its own size. The remove mark is a bare button around a glyph in the
   tertiary ink. Add is a 9px radius block with a 1px card border on the surface fill and bold label ink, no vertical padding: it takes
   the height of the field beside it. The suggestion chip is a pill with a dashed separator border, 6px by 10px padding and 12px type. */
export function wedgeMeetingStyle(look: WedgeMeetingLook): CSSProperties {
  const r = wedgeExactRole;
  switch (look) {
    case 'meeting-duration': return {
      minHeight: TOUCH_MIN, margin: '-6px -3px', padding: '6px 3px', border: 'none', background: 'transparent', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', fontFamily: 'inherit',
    };
    case 'meeting-toggle': return {
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: TOUCH_MIN, margin: '-4px 0', padding: '4px 0',
      border: 'none', background: 'transparent', cursor: 'pointer', WebkitTapHighlightColor: 'transparent', fontFamily: 'inherit',
    };
    case 'meeting-remove': return {
      border: 'none', background: 'transparent', color: r('labelTer'), cursor: 'pointer', padding: 0, display: 'inline-flex',
    };
    case 'meeting-add': return {
      padding: '0 16px', borderRadius: 9, border: `1px solid ${r('cardBorder')}`, background: r('surface'), color: r('label'),
      fontWeight: weight.bold, cursor: 'pointer',
    };
    case 'meeting-suggest': return {
      padding: '6px 10px', borderRadius: radius.pill, border: `1px dashed ${r('separator')}`, background: 'transparent',
      color: r('labelSec'), fontSize: font.caption, cursor: 'pointer',
    };
  }
}

export type WedgeSheetAreaVariant = 'wedge-sheet-area';

export const isWedgeSheetAreaVariant = (variant: unknown): variant is WedgeSheetAreaVariant => variant === 'wedge-sheet-area';

/* Released notes field of the Schedule meeting sheet (Agenda / notes): the title, date, time, guest and place fields beside it
   (`wedge-sheet-field`) as a textarea, and the user may drag the height. The caller owns the value, the placeholder, the row count
   (3) and the handler, which slices the text. */
export function wedgeSheetAreaStyle(_variant: WedgeSheetAreaVariant = 'wedge-sheet-area'): CSSProperties {
  return {...wedgeFormFieldStyle('wedge-sheet-field'), resize: 'vertical'};
}
